import { useNavigate } from 'react-router-dom'
import { useParams } from 'react-router-dom'

import { Features } from 'generated/featureDefinitions'
import { ProjectEmployeesDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  BulkAssignProjectWorkerDocument,
  ChangeAssignmentProjectDocument,
  ProjectManagementProjectDetailsPageAssignEmployeesSingleProjectDocument,
  ProjectPeopleDataCollectionDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { LoadingState } from 'modules/project_management/components/LoadingState'
import AssignEmployeesSingleProjectModal from 'modules/project_management/components/modals/AssignEmployeesSingleProjectModal'

const AssignEmployeesSingleProjectModalPage = () => {
  const { company } = useCurrentSession()
  const { id } = useParams()
  const { loading: loadingQuery, data: projectsData } = useQuery(
    ProjectManagementProjectDetailsPageAssignEmployeesSingleProjectDocument,
    {
      variables: {
        projectId: Number(id),
        companyId: company.id,
        featureIds: [Features.projects_overview],
      },
      fetchPolicy: 'no-cache',
    }
  )

  const hasProjects2_0 = !!projectsData?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_overview
  )?.available

  const dataCollectionsEnabled =
    projectsData?.features.featuresConnection.nodes.find(
      (node) => node.name === 'dev_projects_data_collections'
    )?.enabled ?? false

  const project = projectsData?.projectManagement.project

  const navigate = useNavigate()

  const refetchQueries = dataCollectionsEnabled
    ? [ProjectPeopleDataCollectionDocument]
    : [ProjectEmployeesDataTableDocument]

  const [bulkAssign, { loading: loadingBulkAssign }] = useMutation(
    BulkAssignProjectWorkerDocument,
    {
      extractorFn: (data) =>
        data.projectManagementMutations.bulkAssignProjectmanagementProjectWorker,
      refetchQueries: refetchQueries,
      awaitRefetchQueries: true,
    }
  )
  const [changeAssignment, { loading: loadingAssignment }] = useMutation(
    ChangeAssignmentProjectDocument,
    {
      extractorFn: (data) =>
        data.projectManagementMutations.changeAssignmentProjectmanagementProject,
      refetchQueries: refetchQueries,
      awaitRefetchQueries: true,
    }
  )

  if (loadingQuery) return <LoadingState />

  if (!project) return null

  const canAssignEmployees = project._policies?.assignUnassignProjectWorkers.can || false

  if (!canAssignEmployees) return null

  const assignedEmployees =
    project.projectWorkers.flatMap((pw) => {
      return pw.employee && pw.assigned
        ? [
            {
              id: pw.employee.id,
              firstName: pw.employee.firstName,
              fullName: pw.employee.fullName,
              lastName: pw.employee.lastName,
              access: {
                id: pw.employee.access.id,
              },
            },
          ]
        : []
    }) || []

  const employeesAssignment = project.employeesAssignment || 'manual'

  const onSubmit = async (
    oldEmployeesAssignment: string,
    newEmployeesAssignment: string,
    selectedEmployees: number[]
  ) => {
    track(trackingEvents.projectManagement.projectWorker.assignEmployeesToProjectSubmit)

    const responseAssignment =
      oldEmployeesAssignment !== newEmployeesAssignment
        ? await changeAssignment({
            variables: { id: Number(id), employeesAssignment: newEmployeesAssignment },
          })
        : undefined

    const responseAssign =
      newEmployeesAssignment === 'manual'
        ? await bulkAssign({
            variables: {
              employeeIds: selectedEmployees,
              projectIds: [Number(id)],
              notifyEmployees: hasProjects2_0,
            },
          })
        : undefined

    if (responseAssign) {
      handleResult(responseAssign, () => undefined)
    } else {
      if (responseAssignment) handleResult(responseAssignment, () => undefined)
    }

    onClose()
  }

  const onClose = () => {
    navigate(-1)
  }

  return (
    <AssignEmployeesSingleProjectModal
      onClose={onClose}
      onSubmit={onSubmit}
      loading={loadingBulkAssign || loadingAssignment}
      title={i18n.t('project_management.modals.assign_employees.title')}
      description={i18n.t('project_management.modals.assign_employees.singleDescription')}
      iconName={'add-user'}
      initialAssignedEmployees={assignedEmployees}
      employeesAssignment={employeesAssignment}
      submitButton={i18n.t('project_management.modals.assign_employees.submit')}
    />
  )
}

export default AssignEmployeesSingleProjectModalPage
